// BUILD_TIMESTAMP: 1738321200
import { HashRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import { Header } from './components/Header/Header';
import { CTROverlay } from './components/CTROverlay/CTROverlay';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import { PINProtectedRoute } from './components/pinauth/PINProtectedRoute';
import { PINLogin } from './components/pinauth/PINLogin';
import { AdminLayout } from './components/admin/AdminLayout';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { AllRedemptions } from './components/admin/AllRedemptions';
import { ManageEvents } from './components/admin/ManageEvents';
import { EmployeeLayout } from './components/employee/EmployeeLayout';
import { EmployeeDashboard } from './components/employee/EmployeeDashboard';
import { EmployeeHistory } from './components/employee/EmployeeHistory';
import { QRCodeDisplay } from './components/employee/QRCodeDisplay';
import { RegisterScanner } from './components/register/RegisterScanner';
import { EventList } from './components/EventList/EventList';
import { PageSections } from './components/PageSections/PageSections';
import { Footer } from './components/Footer/Footer';
import { LandingPage } from './components/LandingPage/LandingPage';
import { useEvents } from './hooks/useEvents';
import styles from './App.module.css';

// ============================================
// PAGE COMPONENTS
// ============================================

function EventListPage() {
  const { events, loading, error, refetch } = useEvents();

  if (error) {
    return (
      <div className={styles.container}>
        <Header />
        <main className={styles.main}>
          <p className={styles.error}>FAILED TO LOAD EVENTS</p>
          <button onClick={refetch} className={styles.retryButton}>
            RETRY
          </button>
        </main>
        <Footer />
        <CTROverlay />
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <Header />
        <main className={styles.main}>
          <p className={styles.loading}>LOADING EVENTS...</p>
        </main>
        <Footer />
        <CTROverlay />
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className={styles.container}>
        <Header />
        <main className={styles.main}>
          <p className={styles.message}>NO UPCOMING EVENTS</p>
        </main>
        <Footer />
        <CTROverlay />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Header />
      <main className={styles.main}>
        <EventList events={events} />
        <PageSections />
      </main>
      <Footer />
      <CTROverlay />
    </div>
  );
}

function UnifiedLoginPage() {
  return (
    <LandingPage />
  );
}

function AdminLoginPage() {
  return (
    <div className={styles.container}>
      <PINLogin requireAdmin={true} />
      <CTROverlay />
    </div>
  );
}

function EmployeeLoginPage() {
  return (
    <div className={styles.container}>
      <PINLogin requireAdmin={false} />
      <CTROverlay />
    </div>
  );
}

function AdminDashboardPage() {
  return (
    <AdminLayout>
      <AdminDashboard />
    </AdminLayout>
  );
}

function AllRedemptionsPage() {
  return (
    <AdminLayout>
      <AllRedemptions />
    </AdminLayout>
  );
}

function ManageEventsPage() {
  return (
    <AdminLayout>
      <ManageEvents />
    </AdminLayout>
  );
}

function EmployeeDashboardPage() {
  return (
    <EmployeeLayout>
      <EmployeeDashboard />
    </EmployeeLayout>
  );
}

function RegisterScannerPage() {
  return (
    <div className={styles.container}>
      <RegisterScanner />
    </div>
  );
}

function QRCodeDisplayPage() {
  return (
    <EmployeeLayout>
      <QRCodeDisplay />
    </EmployeeLayout>
  );
}

function EmployeeHistoryPage() {
  return (
    <EmployeeLayout>
      <EmployeeHistory />
    </EmployeeLayout>
  );
}

// ============================================
// MAIN APP
// ============================================

function App() {
  console.log('[REFUGE] App rendering');
  return (
    <ErrorBoundary>
      <HashRouter>
        <Routes>
          {/* Public Event List - Main page (must be first) */}
          <Route path="/" element={<Navigate to="/events" replace />} />
          <Route path="/events" element={<EventListPage />} />

          {/* Old id-based employee links redirect to the dashboard.
              (React Router v6 matches case-insensitively and ignores trailing
              slashes, so no uppercase or trailing-slash variants are needed —
              adding them shadows the real routes and self-redirects to a
              blank screen.) */}
          <Route path="/employee/:id" element={<Navigate to="/employee" replace />} />

          {/* Unified Login Route */}
          <Route path="/login" element={<UnifiedLoginPage />} />

          {/* PIN Login Routes (direct access) */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/employee/login" element={<EmployeeLoginPage />} />

          {/* Admin Routes - PIN Protected */}
          <Route
            path="/admin"
            element={
              <PINProtectedRoute requireAdmin={true} redirectTo="/admin/login">
                <AdminDashboardPage />
              </PINProtectedRoute>
            }
          />
          <Route
            path="/admin/redemptions"
            element={
              <PINProtectedRoute requireAdmin={true} redirectTo="/admin/login">
                <AllRedemptionsPage />
              </PINProtectedRoute>
            }
          />
          <Route
            path="/admin/events"
            element={
              <PINProtectedRoute requireAdmin={true} redirectTo="/admin/login">
                <ManageEventsPage />
              </PINProtectedRoute>
            }
          />

          {/* Employee Routes - PIN Protected */}
          <Route
            path="/employee"
            element={
              <PINProtectedRoute redirectTo="/employee/login">
                <EmployeeDashboardPage />
              </PINProtectedRoute>
            }
          />
          <Route
            path="/employee/history"
            element={
              <PINProtectedRoute redirectTo="/employee/login">
                <EmployeeHistoryPage />
              </PINProtectedRoute>
            }
          />
          <Route
            path="/employee/qr"
            element={
              <PINProtectedRoute redirectTo="/employee/login">
                <QRCodeDisplayPage />
              </PINProtectedRoute>
            }
          />

          {/* Register Scanner - Public (no auth required for kiosk mode) */}
          <Route path="/register" element={<RegisterScannerPage />} />

          {/* Fallback */}
          <Route path="*" element={
            <div style={{ padding: '50px', color: 'white', textAlign: 'center' }}>
              <h1>404 - ROUTE NOT FOUND</h1>
              <p>Current Path: {window.location.hash}</p>
              <Link to="/" style={{ color: '#7b68ee' }}>GO HOME</Link>
            </div>
          } />
        </Routes>
      </HashRouter>
    </ErrorBoundary>
  );
}

export default App;
// Force rebuild - $(date)
